import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Handles the OAuth redirect back from the server.
 * Token arrives in the URL fragment (#access_token=...) so it never hits server logs.
 * Also supports legacy #token=... format during transition.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState('');
  const [returnTo, setReturnTo] = useState('/');

  useEffect(() => {
    const hash = window.location.hash.slice(1); // strip leading #
    const params = new URLSearchParams(hash);

    // Clear the fragment so the token is never bookmarked or shared
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    // Support both new (access_token) and legacy (token) formats
    const token = params.get('access_token') || params.get('token');
    const errorMsg = params.get('error');
    const savedReturnTo = sessionStorage.getItem('nr_returnTo') || '/';
    sessionStorage.removeItem('nr_returnTo');

    if (errorMsg) {
      // If this was a link-account flow, redirect back silently rather than
      // showing a dead-end error page with no navbar.
      if (savedReturnTo !== '/') {
        navigate(savedReturnTo, { replace: true });
        return;
      }
      setError(decodeURIComponent(errorMsg));
      return;
    }

    if (!token) {
      setError('Authentication failed — no token received.');
      return;
    }

    localStorage.setItem('nr_token', token);
    refreshUser()
      .then(() => {
        navigate(savedReturnTo, { replace: true });
      })
      .catch(() => {
        setError('Authentication succeeded but failed to load user. Please try signing in again.');
      });
  }, []); // eslint-disable-line

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div className="card" style={{ maxWidth: 400, textAlign: 'center' }}>
          <h2 style={{ marginBottom: 12, color: 'var(--danger)' }}>Sign In Failed</h2>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24 }}>{error}</p>
          <button className="btn btn--primary" onClick={() => navigate(returnTo, { replace: true })}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p className="text-muted" style={{ fontSize: 14 }}>Signing you in…</p>
    </div>
  );
}
